import React, { useState } from 'react';
import { PencilLine, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { competitorsAPI } from '../services/api';
import { hostOf } from './CompetitorPositionBadge';

// Type the competitor prices by hand (no ScrapingBee credit used); stored as a snapshot like a scan
const CompetitorManualPrices = ({ productId, urls, latest, onSaved }) => {
  const slots = (urls || []).filter(Boolean);
  const prevBySlot = Object.fromEntries((latest?.competitors || []).map((c) => [c.slot, c]));
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);
  if (!slots.length) return null;

  const set = (slot, key) => (e) => setRows((r) => ({ ...r, [slot]: { ...(r[slot] || {}), [key]: e.target.value } }));
  const parse = (v) => { const n = parseFloat(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : null; };

  const save = async () => {
    const prices = slots.map((_, i) => ({ slot: i + 1, ...(rows[i + 1] || {}) })).filter((r) => parse(r.price) > 0)
      .map((r) => ({ slot: r.slot, price: parse(r.price), old_price: parse(r.old_price) || null, shipping: parse(r.shipping), availability: (r.availability || '').trim() || null }));
    if (!prices.length) { toast.error('Saisissez au moins un prix'); return; }
    setSaving(true);
    try {
      const r = await competitorsAPI.setManualPrices(productId, prices);
      toast.success(r.message);
      setRows({}); setOpen(false);
      onSaved?.(r.snapshot);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border border-dashed border-amber-400 rounded-lg p-4 bg-amber-50/40" data-testid="competitor-manual-prices">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h4 className="font-semibold text-gray-900 flex items-center gap-2"><PencilLine size={16} className="text-amber-600" /> Saisie manuelle des prix concurrents</h4>
          <p className="text-xs text-gray-600 mt-0.5">Relevez les prix vous-même : aucun crédit ScrapingBee consommé. Les emplacements laissés vides conservent le dernier prix connu.</p>
        </div>
        {!open && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="competitor-manual-open" className="border-amber-400 text-amber-800 hover:bg-amber-100">Saisir les prix</Button>}
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          <div className="hidden sm:grid grid-cols-12 gap-2 text-[11px] font-medium text-gray-500 px-1"><span className="col-span-4">Concurrent</span><span className="col-span-2">Prix TTC (€)</span><span className="col-span-2">Ancien prix</span><span className="col-span-2">Livraison (€)</span><span className="col-span-2">Dispo</span></div>
          {slots.map((url, i) => {
            const slot = i + 1; const prev = prevBySlot[slot]; const r = rows[slot] || {};
            return (
              <div key={slot} className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-center" data-testid={`competitor-manual-row-${slot}`}>
                <div className="col-span-2 sm:col-span-4 min-w-0">
                  <a href={url} target="_blank" rel="noreferrer" className="text-sm text-blue-700 hover:underline truncate block">C{slot} · {hostOf(url)}</a>
                  {prev?.price != null && <p className="text-[11px] text-gray-500">Dernier prix connu : {Number(prev.price).toFixed(2)} €{prev.method === 'manual' ? ' (saisi)' : ''}</p>}
                </div>
                <Input type="number" step="0.01" min="0.01" placeholder={prev?.price != null ? Number(prev.price).toFixed(2) : '0.00'} value={r.price || ''} onChange={set(slot, 'price')} className="sm:col-span-2 h-9" data-testid={`competitor-manual-price-${slot}`} />
                <Input type="number" step="0.01" min="0.01" placeholder="—" value={r.old_price || ''} onChange={set(slot, 'old_price')} className="sm:col-span-2 h-9" data-testid={`competitor-manual-old-${slot}`} />
                <Input type="number" step="0.01" min="0" placeholder="—" value={r.shipping || ''} onChange={set(slot, 'shipping')} className="sm:col-span-2 h-9" data-testid={`competitor-manual-shipping-${slot}`} />
                <Input placeholder="En stock" maxLength={60} value={r.availability || ''} onChange={set(slot, 'availability')} className="sm:col-span-2 h-9" data-testid={`competitor-manual-availability-${slot}`} />
              </div>
            );
          })}
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setRows({}); }} data-testid="competitor-manual-cancel">Annuler</Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="competitor-manual-save">{saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Enregistrer les prix relevés</Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default CompetitorManualPrices;
